// 1. Framework & Library imports
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";

// 2. Project-specific imports (API, types, helpers)
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  FleetVehiclePicture,
  FleetVehiclePictureRow,
} from "services/api/fleet/vehicles/pictures/types";
import { getFleetVehiclePicturesAPI } from "services/api/fleet/vehicles/pictures/api";
import { VehicleGalleryCardProps, VehicleGalleryRef } from "./types";

// 3. UI Components


// 4. Constants
import {
  FLEET_VEHICLE_PICTURE_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_PICTURE_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { transformFleetVehiclePicture } from "services/api/fleet/vehicles/pictures/transformations";
import { useTranslate } from "helpers";
import VehiclePictureEditCard from "./components/VehiclePictureEditCard/VehiclePictureEditCard";
import VehiclePicturePreview from "./components/VehiclePicturePreview/VehiclePicturePreview";
import { NoResults } from "ui/components/NoResults";

const VehicleGalleryCard = forwardRef<VehicleGalleryRef, VehicleGalleryCardProps>((props, ref) => {
  const { fleetVehicleDetail, onPictureChange } = props;
  const { t } = useTranslate();

  const thumbsContainerRef = useRef<HTMLDivElement | null>(null);
  const [slidesPerView, setSlidesPerView] = useState<number>(3);

  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [selectedPicture, setSelectedPicture] =
    useState<FleetVehiclePicture | null>(null);
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehiclePictureRow>
  >(initialDataWithPagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    openAddNew: () => {
      setSelectedPicture(null);
      setShowAddNewModal(true);
    },
    refresh: () => {
      if (fleetVehicleDetail.id)
        fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    },
  }));

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  useEffect(() => {
    if (!thumbsContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const thumbWidth = Math.max(height * 1.2, 80);
      const calculatedSlides = Math.max(1, Math.floor(width / thumbWidth));
      setSlidesPerView(calculatedSlides);
    });

    observer.observe(thumbsContainerRef.current);

    return () => observer.disconnect();
  }, [searchResults.data.length]);


  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehiclePicturesAPI(
        vehicleId,
        pagination,
        ""
      );

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleAddNewClick = useCallback((e: any) => {
    setSelectedPicture(null);
    setShowAddNewModal(true);
  }, []);

  const handlePreviewClick = useCallback(
    (picture: FleetVehiclePictureRow | null) => {
      setSelectedPicture(transformFleetVehiclePicture(picture));
      setShowPreviewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    onPictureChange?.();
  }, [fleetVehicleDetail.id, onPictureChange]);

  const handleDeleteSuccess = useCallback(() => {
    setShowPreviewModal(false);
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    onPictureChange?.();
  }, [fleetVehicleDetail.id, onPictureChange]);

  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="h-full relative overflow-hidden">
        {searchResults.data.length === 0 ? (
          <NoResults message={t("admin.fleet.vehicle.gallery.noresults")} />
        ) : (
          <div className="w-full h-full mx-auto p-3 flex flex-col">
            <div className="flex-1 min-h-0">
              <Swiper
                spaceBetween={10}
                navigation
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                modules={[Navigation, Thumbs]}
                className="main-slider h-full"
                style={{ "--swiper-navigation-color": "#ffffff" } as React.CSSProperties}
              >
                {searchResults.data.map((picture) => (
                  <SwiperSlide key={picture.id} onClick={() => handlePreviewClick(picture)} className="cursor-pointer">
                    <div className="bg-slate-50 relative w-full h-full rounded-md overflow-hidden">
                      <img
                        src={picture.url}
                        alt={picture.upload.originalFileName}
                        className="absolute top-0 left-0 w-full h-full object-cover"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div ref={thumbsContainerRef} className="mt-3 flex gap-2 h-[20%] min-h-[60px] max-h-[150px] flex-shrink-0">
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={6}
                slidesPerView={slidesPerView}
                watchSlidesProgress
                modules={[Thumbs]}
                className="thumbnail-slider flex-1"
              >
                {searchResults.data.map((picture) => (
                  <SwiperSlide key={picture.id} className="cursor-pointer  ">
                    <img
                      src={picture.url}
                      alt={picture.upload.originalFileName}
                      className="object-cover rounded-md overflow-hidden h-full w-full"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
      </div>

      <VehiclePicturePreview
        show={showPreviewModal}
        vehicleId={fleetVehicleDetail.id}
        pictureId={selectedPicture?.id ?? null}
        onSuccess={handleDeleteSuccess}
        onChange={() => {
          if (fleetVehicleDetail.id)
            fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
          onPictureChange?.();
        }}
        onClose={() => setShowPreviewModal(false)}
      />
      <VehiclePictureEditCard
        show={showAddNewModal}
        title={
          selectedPicture?.id
            ? FLEET_VEHICLE_PICTURE_EDIT_PAGE_TITLE.text
            : FLEET_VEHICLE_PICTURE_CREATE_PAGE_TITLE.text
        }
        vehicleName={fleetVehicleDetail.name ?? ""}
        vehicleId={fleetVehicleDetail.id}
        pictureId={selectedPicture?.id ?? null}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
    </>
  );
});

export default memo(VehicleGalleryCard);
